import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { CalendarClock, Check, ChevronDown, Copy, Headphones, Info, KeyRound, LifeBuoy, ListChecks, MessageCircle, Package, PackagePlus, Paperclip, RefreshCw, Send, ShoppingBag, Trophy, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const SUPPORT_CSS = `
  @keyframes pmSupportFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
  .pm-support-fab.pm-idle { animation: pmSupportFloat 3.6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .pm-support-fab.pm-idle { animation: none !important; }
  }
`;

function CopyTvBtn({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const disabled = !value;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("TradingView username copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={disabled}
      data-testid={testId}
      title={disabled ? "No username to copy" : "Copy TradingView username"}
      className="inline-flex items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 text-[10px] text-[#E2E8F0] hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function CopyBtn({ value, testId, label = "Copy", okMsg = "Copied" }) {
  const [copied, setCopied] = useState(false);
  const disabled = !value;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(okMsg);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={disabled}
      data-testid={testId}
      title={disabled ? "Nothing to copy" : label}
      className="inline-flex items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 text-[10px] text-[#E2E8F0] hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : label}
    </button>
  );
}

const MEMBER_QUICK = [
  { key: "dashboard", label: "My Dashboard" },
  { key: "rank", label: "My Rank" },
  { key: "rewards", label: "My Rewards" },
  { key: "indicator_status", label: "My Indicators" },
  { key: "pm_coins", label: "Coins" },
  { key: "how_it_works", label: "How It Works" },
];

const ADMIN_QUICK = [
  { key: "asst_pending", label: "Indicator pending request" },
  { key: "asst_renewals", label: "Indicator Renewal" },
  { key: "asst_regs", label: "Client register hari ni" },
  { key: "asst_ib", label: "Find Clients by IB" },
  { key: "asst_draft", label: "Create Reward Draft" },
  { key: "asst_redemptions", label: "Redemptions" },
  { key: "asst_luckydraw", label: "Lucky Draw Winners" },
  { key: "asst_indaccess", label: "Indicator Access" },
];

const CATEGORIES = ["Merchandise", "Luxury", "Technology", "Trading Equipment", "Lifestyle", "Experiences"];

const shortId = (id) => (id || "").slice(-6);

function AsstCard({ kind, data, onToast }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({});
  const [ranks, setRanks] = useState({});

  const approve = async (r) => {
    if (r.is_renewal) { onToast("Renewals need a manual expiry — use the Indicators page."); return; }
    const msg = r.claim_locked_30d
      ? `Approve claim for ${r.member_name} (${r.member_uid})?\n\nStarts: ${r.approval_start_myt}\nExpires: ${r.approval_expiry_myt}\n\n${r.tradingview_manual_notice}`
      : `Approve purchase for ${r.member_name}? Product duration applies.\n\n${r.tradingview_manual_notice}`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await api.post(`/admin/indicator-requests/${r.id}/approve`, { remark: "" }); setDone((d) => ({ ...d, [r.id]: "approved" })); onToast("Approved"); }
    catch (e) { onToast(formatApiError(e)); } finally { setBusy(false); }
  };
  const reject = async (r) => {
    const reason = window.prompt("Reject reason (sent to the client):", "Please double check your TRADINGVIEW username or contact your IB");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/indicator-requests/${r.id}/reject`, { reason: reason.trim() }); setDone((d) => ({ ...d, [r.id]: "rejected" })); onToast("Rejected — client notified"); }
    catch (e) { onToast(formatApiError(e)); } finally { setBusy(false); }
  };
  const approveRenewal = async (r) => {
    if (!window.confirm(`Approve renewal for ${r.member_name || "—"} (${r.member_uid || "—"})?\n\nThis grants 30 days of access starting now.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/indicator-requests/${r.id}/approve`, { remark: "" });
      const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kuala_Lumpur" }) : "—");
      setDone((d) => ({ ...d, [r.id]: `approved · ${fmt(data.start_date)} → ${fmt(data.expiry_date)}` }));
      onToast(`Renewal approved — access ${fmt(data.start_date)} to ${fmt(data.expiry_date)}`);
    } catch (e) { onToast(formatApiError(e)); } finally { setBusy(false); }
  };
  const addProgress = async (c) => {
    const raw = window.prompt(`Add progress for ${c.name} (current ${c.progress}). Enter amount to ADD:`);
    const val = parseFloat(raw);
    if (!raw || isNaN(val) || val <= 0) return;
    if (!window.confirm(`Add ${val} to ${c.name}?\nCurrent: ${c.progress}  →  New: ${c.progress + val}`)) return;
    const opId = crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    setBusy(true);
    try { const { data: res } = await api.post(`/admin/members/${c.user_id}/progress`, { mode: "add", value: val, operation_id: opId }); setRanks((p) => ({ ...p, [c.user_id]: res })); onToast("Progress added"); }
    catch (e) { onToast(formatApiError(e)); } finally { setBusy(false); }
  };
  const revokeAccess = async (g) => {
    if (!window.confirm(`Revoke "${g.indicator_name}" access? The member will lose access to this indicator.`)) return;
    setBusy(true);
    try { await api.patch(`/admin/indicator-access/${g.access_id}/revoke`); setDone((d) => ({ ...d, [g.access_id]: "revoked" })); onToast("Access revoked"); }
    catch (e) { onToast(formatApiError(e)); } finally { setBusy(false); }
  };

  if (kind === "asst_regs") {
    return (
      <div className="mt-2 space-y-2" data-testid="asst-regs">
        <p className="text-xs text-[#64748B]">Registered today ({data.date_myt}) — {data.count}</p>
        {data.members.map((m) => (
          <div key={m.user_id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs" data-testid={`asst-reg-${m.uid}`}>
            <div className="text-white font-medium">{m.name} · {m.uid}</div>
            <div className="text-[#94A3B8]">{m.email}</div>
            <div className="text-[#64748B] mt-1">{m.registered_at_myt} · {m.rank} · IB: {m.sponsoring_ib}</div>
          </div>
        ))}
        {data.count === 0 && <p className="text-xs text-[#64748B]">No registrations today.</p>}
      </div>
    );
  }
  if (kind === "asst_pending") {
    return (
      <div className="mt-2 space-y-2" data-testid="asst-pending">
        {data.length === 0 && <p className="text-xs text-[#64748B]">No pending indicator requests.</p>}
        {data.map((r) => (
          <div key={r.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs" data-testid={`asst-req-${r.id}`}>
            <div className="text-white font-medium">{r.member_name || "—"} · {r.member_uid || "—"}</div>
            <div className="text-[#94A3B8]">{r.email || "—"}</div>
            <div className="text-[#94A3B8]">IB: <span className="font-bold text-[#D4AF37]">{r.ib_name || "—"}</span></div>
            <div className="text-[#E2E8F0] mt-1"><span className="font-bold text-[#D4AF37]">{r.indicator_name}</span> · {r.type || "claim"} · <span className="text-[#D4AF37]">{r.status}</span></div>
            <div className="text-[#64748B] mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <span>Broker: <span className="font-bold text-[#E2E8F0]">{r.registered_broker || "—"}</span></span>
              <span>· TV: <span className="font-bold text-white break-all">{r.tradingview_username || "—"}</span></span>
              <CopyTvBtn value={r.tradingview_username} testId={`asst-req-${r.id}-tv-copy`} />
            </div>
            {r.claim_locked_30d && <div className="text-[#64748B] mt-1">Approve → {r.approval_start_myt} to {r.approval_expiry_myt}</div>}
            <div className="text-[10px] text-amber-400/80 mt-1">{r.tradingview_manual_notice}</div>
            {done[r.id] ? (
              <div className="mt-2 text-emerald-400">✓ {done[r.id]}</div>
            ) : r.awaiting_ib_review ? (
              <div className="mt-2 text-[#64748B]">Awaiting IB review — not admin-approvable.</div>
            ) : r.actionable ? (
              <div className="mt-2 flex gap-2">
                <button disabled={busy} onClick={() => approve(r)} className="btn-gold px-3 py-1.5 rounded-md" data-testid={`asst-approve-${r.id}`}>Approve</button>
                <button disabled={busy} onClick={() => reject(r)} className="px-3 py-1.5 rounded-md border border-red-400/40 text-red-400" data-testid={`asst-reject-${r.id}`}>Reject</button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
  if (kind === "asst_renewals") {
    return (
      <div className="mt-2 space-y-2" data-testid="asst-renewals">
        {data.length === 0 && <p className="text-xs text-[#64748B]" data-testid="asst-renewals-empty">No pending indicator renewal requests.</p>}
        {data.map((r) => (
          <div key={r.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs" data-testid={`asst-renewal-${r.id}`}>
            <div className="text-white font-medium">{r.member_name || "—"} · {r.member_uid || "—"}</div>
            <div className="text-[#94A3B8] flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-0.5">
              <span className="break-all" data-testid={`asst-renewal-${r.id}-email`}>{r.email || "—"}</span>
              <CopyBtn value={r.email} label="Copy Email" okMsg="Email copied" testId={`asst-renewal-${r.id}-email-copy`} />
            </div>
            <div className="text-[#94A3B8]">IB: <span className="font-bold text-[#D4AF37]">{r.ib_name || "—"}</span></div>
            <div className="text-[#E2E8F0] mt-1"><span className="font-bold text-[#D4AF37]">{r.indicator_name}</span> · renewal · <span className="text-[#D4AF37]">{r.status}</span></div>
            <div className="text-[#64748B] mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <span>Broker: <span className="font-bold text-[#E2E8F0]">{r.registered_broker || "—"}</span></span>
              <span>· TV: <span className="font-bold text-white break-all" data-testid={`asst-renewal-${r.id}-tv`}>{r.tradingview_username || "—"}</span></span>
              <CopyTvBtn value={r.tradingview_username} testId={`asst-renewal-${r.id}-tv-copy`} />
            </div>
            <div className="text-[#64748B] mt-0.5">Submitted: {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kuala_Lumpur" }) : "—"}</div>
            {done[r.id] ? (
              <div className="mt-2 text-emerald-400" data-testid={`asst-renewal-${r.id}-done`}>✓ {done[r.id]}</div>
            ) : (
              <div className="mt-2 flex gap-2">
                <button disabled={busy} onClick={() => approveRenewal(r)} className="btn-gold px-3 py-1.5 rounded-md" data-testid={`asst-renewal-approve-${r.id}`}>Approve</button>
                <button disabled={busy} onClick={() => reject(r)} className="px-3 py-1.5 rounded-md border border-red-400/40 text-red-400" data-testid={`asst-renewal-reject-${r.id}`}>Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  if (kind === "asst_ib") {
    if (data.status === "none") return <p className="mt-2 text-xs text-[#64748B]" data-testid="asst-ib-none">No IB matched that name/code.</p>;
    if (data.status === "ambiguous") return (
      <div className="mt-2 text-xs" data-testid="asst-ib-ambiguous">
        <p className="text-[#64748B] mb-1">Multiple IBs matched — refine by exact code:</p>
        {data.ibs.slice(0, 12).map((ib) => <div key={ib.ib_id} className="text-white">• {ib.name} · {ib.uid}</div>)}
        {data.ibs.length > 12 && <p className="text-[#64748B]">…and {data.ibs.length - 12} more</p>}
      </div>
    );
    return (
      <div className="mt-2 space-y-2" data-testid="asst-ib-clients">
        <p className="text-xs text-[#64748B]">Clients under {data.ib.name} ({data.ib.uid}) — {data.count}</p>
        {data.clients.map((c) => {
          const rk = ranks[c.user_id];
          return (
            <div key={c.user_id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs flex items-center justify-between gap-2" data-testid={`asst-client-${c.uid}`}>
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{c.name} · {c.uid}</div>
                <div className="text-[#94A3B8] truncate">{c.email}</div>
                <div className="text-[#64748B]">{rk?.rank?.name || c.rank} · progress {rk?.progress ?? c.progress}</div>
              </div>
              <button disabled={busy} onClick={() => addProgress(c)} className="btn-gold px-3 py-1.5 rounded-md shrink-0" data-testid={`asst-progress-${c.uid}`}>Add Progress</button>
            </div>
          );
        })}
        {data.count === 0 && <p className="text-xs text-[#64748B]">No clients under this IB.</p>}
      </div>
    );
  }
  if (kind === "asst_redemptions") {
    return (
      <div className="mt-2 space-y-2" data-testid="asst-redemptions">
        {data.count === 0 ? (
          <p className="text-xs text-[#64748B]" data-testid="asst-redemptions-empty">No redemptions found.</p>
        ) : (
          <>
            <p className="text-xs text-[#E2E8F0]">Total redemption records: <span className="font-bold text-[#D4AF37]" data-testid="asst-redemptions-count">{data.count}</span></p>
            <button onClick={() => navigate("/admin/rewards", { state: { focus: "redemptions" } })} className="btn-gold px-3 py-1.5 rounded-md text-xs" data-testid="asst-redemptions-open">Open Redemptions</button>
          </>
        )}
      </div>
    );
  }
  if (kind === "asst_luckydraw") {
    return (
      <div className="mt-2 space-y-2" data-testid="asst-luckydraw">
        {data.count === 0 ? (
          <p className="text-xs text-[#64748B]" data-testid="asst-luckydraw-empty">No Lucky Draw winners found.</p>
        ) : (
          <>
            <p className="text-xs text-[#E2E8F0]">Recorded Lucky Draw winners: <span className="font-bold text-[#D4AF37]" data-testid="asst-luckydraw-count">{data.count}</span></p>
            <button onClick={() => navigate("/admin/lucky-draw", { state: { tab: "winners" } })} className="btn-gold px-3 py-1.5 rounded-md text-xs" data-testid="asst-luckydraw-open">Open Winners</button>
          </>
        )}
      </div>
    );
  }
  if (kind === "asst_indaccess") {
    if (!data.found) return <p className="mt-2 text-xs text-[#64748B]" data-testid="asst-indaccess-notfound">No member found with that exact email.</p>;
    return (
      <div className="mt-2 space-y-2" data-testid="asst-indaccess">
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs space-y-0.5" data-testid="asst-indaccess-member">
          <p className="font-bold text-white" data-testid="asst-indaccess-name">{data.member_name}</p>
          <p className="text-[#64748B]" data-testid="asst-indaccess-uid">UID: {data.member_uid}</p>
          <p className="text-[#64748B]" data-testid="asst-indaccess-email">Email: {data.member_email}</p>
          <p className="text-[#64748B]" data-testid="asst-indaccess-ib">Sponsoring IB: {data.sponsoring_ib || "Not recorded"}</p>
        </div>
        {data.grants.length === 0 && <p className="text-xs text-[#64748B]" data-testid="asst-indaccess-empty">No active indicator access found.</p>}
        {data.grants.map((g) => (
          <div key={g.access_id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs flex items-center justify-between gap-2" data-testid={`asst-indaccess-${g.access_id}`}>
            <div className="min-w-0">
              <span className="block font-bold text-[#D4AF37]">{g.indicator_name}</span>
              <span className="block text-[#64748B] mt-0.5" data-testid={`asst-indaccess-tv-${g.access_id}`}>TradingView: {g.tradingview_username || "Not recorded"}</span>
            </div>
            {done[g.access_id] === "revoked" ? (
              <span className="text-emerald-400 shrink-0">✓ revoked</span>
            ) : (
              <button disabled={busy} onClick={() => revokeAccess(g)} className="px-3 py-1.5 rounded-md border border-red-400/40 text-red-400 shrink-0" data-testid={`asst-indaccess-revoke-${g.access_id}`}>Revoke</button>
            )}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function DraftCard({ draft, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = shortId(draft.id);

  const startEdit = () => {
    setForm({
      name: draft.name, category: draft.category, short_description: draft.short_description,
      full_description: draft.full_description, required_rank_level: draft.required_rank_level ?? "",
      stock: draft.stock ?? "", redemption_instructions: draft.redemption_instructions || "",
    });
    setError("");
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.patch(`/support/product-drafts/${draft.id}`, {
        ...form,
        required_rank_level: form.required_rank_level === "" ? null : Number(form.required_rank_level),
        stock: form.stock === "" ? null : Number(form.stock),
      });
      onChange(res.data);
      setEditing(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/support/product-drafts/${draft.id}/confirm`);
      onChange({ ...draft, status: "created" });
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  const retry = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`/support/product-drafts/${draft.id}/retry`);
      onChange(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-[#D4AF37]/30 bg-[#0B0C10] overflow-hidden" data-testid={`support-draft-card-${sid}`}>
      <div className="px-4 py-2.5 border-b border-[#222530] flex items-center justify-between">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37] flex items-center gap-1.5">
          <Package className="h-3 w-3" /> Product Draft
        </p>
        <span className={`text-[10px] font-mono tracking-widest uppercase ${draft.status === "created" ? "text-emerald-400" : draft.status === "failed" ? "text-red-400" : draft.status === "processing" ? "text-[#94A3B8]" : "text-amber-400"}`} data-testid={`support-draft-status-${sid}`}>
          {draft.status === "processing" ? "Analyzing…" : draft.status}
        </span>
      </div>

      {draft.image_url && (
        <div className="px-4 pt-3">
          <img src={`${API}${draft.image_url}`} alt={draft.name || "Product"} className="h-28 w-full object-cover rounded-lg border border-[#222530]" data-testid={`support-draft-image-${sid}`} />
        </div>
      )}

      {draft.error && <p className="px-4 pt-3 text-[11px] text-red-400" data-testid={`support-draft-error-${sid}`}>{draft.error}</p>}

      {!editing ? (
        <div className="px-4 py-3 space-y-1.5 text-xs" data-testid={`support-draft-view-${sid}`}>
          <p className="text-white font-medium text-sm">{draft.name || "—"}</p>
          <p className="text-[#94A3B8]"><span className="text-[#64748B]">Category:</span> {draft.category}</p>
          {draft.short_description && <p className="text-[#94A3B8]">{draft.short_description}</p>}
          {draft.full_description && <p className="text-[#64748B] leading-relaxed">{draft.full_description}</p>}
          <p className="text-[#94A3B8]"><span className="text-[#64748B]">Required rank:</span> {draft.required_rank_level ? `Level ${draft.required_rank_level}` : <span className="text-amber-400">Not set — you must select it</span>}</p>
          <p className="text-[#94A3B8]"><span className="text-[#64748B]">Stock:</span> {draft.stock ?? "Unlimited"}</p>
        </div>
      ) : (
        <div className="px-4 py-3 space-y-2.5" data-testid={`support-draft-edit-${sid}`}>
          <input className="input-lux !py-2 text-xs" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Product name" data-testid={`support-draft-name-${sid}`} />
          <select className="input-lux !py-2 text-xs" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid={`support-draft-category-${sid}`}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input-lux !py-2 text-xs" value={form.short_description} onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))} placeholder="Short description" data-testid={`support-draft-short-${sid}`} />
          <textarea className="input-lux !py-2 text-xs min-h-[64px]" value={form.full_description} onChange={(e) => setForm((f) => ({ ...f, full_description: e.target.value }))} placeholder="Full description" data-testid={`support-draft-full-${sid}`} />
          <div className="flex gap-2">
            <input type="number" min="1" max="36" className="input-lux !py-2 text-xs w-1/2" value={form.required_rank_level} onChange={(e) => setForm((f) => ({ ...f, required_rank_level: e.target.value }))} placeholder="Rank level 1-36 (you select)" data-testid={`support-draft-rank-${sid}`} />
            <input type="number" min="0" className="input-lux !py-2 text-xs w-1/2" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} placeholder="Stock (blank = unlimited)" data-testid={`support-draft-stock-${sid}`} />
          </div>
        </div>
      )}

      {error && <p className="px-4 pb-2 text-[11px] text-red-400">{error}</p>}

      <div className="px-4 pb-3.5 flex gap-2">
        {draft.status === "draft" && !editing && (
          <>
            <button onClick={startEdit} disabled={busy} className="flex-1 py-2 text-[11px] rounded-lg border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50" data-testid={`support-draft-edit-toggle-${sid}`}>
              Edit Fields
            </button>
            <button onClick={confirm} disabled={busy || !draft.required_rank_level} title={!draft.required_rank_level ? "Select the required rank first (Edit Fields)" : ""} className="flex-1 btn-gold py-2 text-[11px] rounded-lg disabled:opacity-50" data-testid={`support-draft-confirm-${sid}`}>
              Confirm & Create Product
            </button>
          </>
        )}
        {editing && (
          <>
            <button onClick={() => setEditing(false)} disabled={busy} className="flex-1 py-2 text-[11px] rounded-lg border border-[#222530] text-[#94A3B8] hover:text-white transition-colors" data-testid={`support-draft-cancel-${sid}`}>Cancel</button>
            <button onClick={save} disabled={busy} className="flex-1 btn-gold py-2 text-[11px] rounded-lg disabled:opacity-50" data-testid={`support-draft-save-${sid}`}>Save Draft</button>
          </>
        )}
        {draft.status === "failed" && (
          <button onClick={retry} disabled={busy} className="flex-1 py-2 text-[11px] rounded-lg border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid={`support-draft-retry-${sid}`}>
            <RefreshCw className="h-3 w-3" /> Retry Analysis
          </button>
        )}
        {draft.status === "created" && (
          <p className="flex-1 text-center text-[11px] text-emerald-400 py-2" data-testid={`support-draft-created-${sid}`}>Created (unpublished) — publish from Reward Management</p>
        )}
      </div>
    </div>
  );
}

function JobProgress({ jobId, onDrafts }) {
  const [job, setJob] = useState(null);
  const sid = shortId(jobId);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const res = await api.get(`/support/import-jobs/${jobId}`);
        if (!active) return;
        setJob(res.data);
        if (res.data.status !== "processing") {
          const drafts = await api.get(`/support/product-drafts?job_id=${jobId}`);
          if (active) onDrafts(drafts.data);
          return;
        }
        setTimeout(poll, 2000);
      } catch {}
    };
    poll();
    return () => { active = false; };
  }, [jobId]);

  const done = (job?.completed || 0) + (job?.failed || 0);
  return (
    <div className="rounded-xl border border-[#222530] bg-[#0B0C10] px-4 py-3" data-testid={`support-job-progress-${sid}`}>
      <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37] mb-1.5">Processing products</p>
      <p className="text-xs text-[#94A3B8] font-mono" data-testid={`support-job-count-${sid}`}>{done} / {job?.total ?? "…"} completed{job?.failed ? ` (${job.failed} failed)` : ""}</p>
      <div className="mt-2 h-1 rounded-full bg-[#222530] overflow-hidden">
        <div className="h-full bg-[#D4AF37] transition-all" style={{ width: job?.total ? `${(done / job.total) * 100}%` : "5%" }} />
      </div>
      {job && job.status !== "processing" && (
        <p className="mt-2 text-[11px] text-emerald-400" data-testid={`support-job-done-${sid}`}>
          {job.failed ? `Finished with ${job.failed} failed item(s) — retry them below.` : "All drafts ready for review."}
        </p>
      )}
    </div>
  );
}

function DraftMessage({ draftId }) {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    api.get(`/support/product-drafts?draft_id=${draftId}`).then((res) => setDraft(res.data[0] || null)).catch(() => {});
  }, [draftId]);
  if (!draft) return null;
  return <div className="mt-2"><DraftCard draft={draft} onChange={setDraft} /></div>;
}

function JobMessage({ jobId }) {
  const [drafts, setDrafts] = useState([]);
  const updateDraft = (d) => setDrafts((list) => list.map((x) => (x.id === d.id ? d : x)));
  return (
    <div className="mt-2 space-y-2">
      <JobProgress jobId={jobId} onDrafts={setDrafts} />
      {drafts.map((d) => <DraftCard key={d.id} draft={d} onChange={updateDraft} />)}
    </div>
  );
}

function HelperMemberCard({ preview }) {
  const [state, setState] = useState("preview");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const save = async () => {
    setState("saving");
    setError("");
    try {
      const res = await api.post("/support/helper/member/save", preview.payload);
      setResult(res.data);
      setState("done");
    } catch (err) {
      setError(formatApiError(err));
      setState("preview");
    }
  };

  if (state === "done") {
    return (
      <div className="mt-2 rounded-xl border border-emerald-500/30 bg-[#0B0C10] px-4 py-3 text-xs space-y-1" data-testid="helper-member-done">
        <p className="text-emerald-400 font-medium">Member successfully added.</p>
        <p className="text-[#E2E8F0]">Username: <span className="text-white">{result.member.username}</span></p>
        <p className="text-[#E2E8F0]">Assigned IB: <span className="font-mono text-[#D4AF37]">{result.ib.referral_code}</span></p>
        <p className="text-[#E2E8F0]">Rank: <span className="text-white">{result.rank.name}</span></p>
      </div>
    );
  }
  if (state === "cancelled") {
    return <p className="mt-2 text-[11px] text-[#64748B]" data-testid="helper-member-cancelled">Cancelled — nothing was saved.</p>;
  }
  return (
    <div className="mt-2 rounded-xl border border-[#D4AF37]/30 bg-[#0B0C10] px-4 py-3 text-xs space-y-1.5" data-testid="helper-member-preview">
      <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">Member Preview</p>
      <p className="text-[#94A3B8]">Username: <span className="text-white">{preview.username}</span></p>
      <p className="text-[#94A3B8]">Full Name: <span className="text-white">{preview.full_name}</span></p>
      <p className="text-[#94A3B8]">Email: <span className="text-white">{preview.email}</span></p>
      <p className="text-[#94A3B8]">Broker: <span className="text-white">{preview.registered_broker}</span></p>
      <p className="text-[#94A3B8]">Sponsored By: <span className="font-mono text-[#D4AF37]">{preview.ib_code}</span> <span className="text-[#64748B]">({preview.ib_name})</span></p>
      <p className="text-[#94A3B8]">Rank Progress: <span className="text-white">{preview.rank_progress}</span></p>
      <p className="text-[#94A3B8]">Detected Rank: <span className="text-[#D4AF37] font-medium">{preview.detected_rank}</span>{preview.next_rank ? <span className="text-[#64748B]"> ({preview.progress_pct}% to {preview.next_rank})</span> : ""}</p>
      <p className="text-emerald-400 font-mono text-[10px] tracking-widest uppercase">Status: Ready to Save</p>
      {error && <p className="text-[11px] text-red-400" data-testid="helper-member-error">{error}</p>}
      <div className="flex gap-2 pt-1">
        <button onClick={() => setState("cancelled")} disabled={state === "saving"} className="flex-1 py-2 text-[11px] rounded-lg border border-[#222530] text-[#94A3B8] hover:text-white transition-colors" data-testid="helper-member-cancel">Cancel</button>
        <button onClick={save} disabled={state === "saving"} className="flex-1 btn-gold py-2 text-[11px] rounded-lg disabled:opacity-50" data-testid="helper-member-save">{state === "saving" ? "Saving…" : "SAVE"}</button>
      </div>
    </div>
  );
}

export default function SupportChat({ role, fullPage = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const dragState = useRef(null);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem("pm_support_hidden") === "1"; } catch { return false; }
  });
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(max-width: 767px)").matches : false);
  const [mobileBox, setMobileBox] = useState(null);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const followRef = useRef(true);
  const pageRef = useRef(null);
  const composerRef = useRef(null);
  const clientInputRef = useRef(null);
  const mobileActive = fullPage && isMobile;
  const prefersReduced = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        if (!p || !containerRef.current) return p;
        const rect = containerRef.current.getBoundingClientRect();
        const m = 12;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        return {
          right: Math.min(Math.max(p.right, m), Math.max(m, vw - rect.width - m)),
          bottom: Math.min(Math.max(p.bottom, m), Math.max(m, vh - rect.height - m)),
        };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // Mobile Support: anchor the chat to the VISUAL viewport as a fixed panel so the composer
  // always sits above the iOS keyboard and only the conversation scrolls. Restores on leave.
  useEffect(() => {
    if (!mobileActive) { setMobileBox(null); return undefined; }
    document.body.classList.add("pm-support-lock");
    const vv = window.visualViewport;
    const apply = () => {
      const vh = vv ? vv.height : window.innerHeight;
      const offTop = vv ? vv.offsetTop : 0;
      const keyboardOpen = window.innerHeight - vh > 150;
      const navEl = document.querySelector('[data-testid^="mobile-bottom-nav"]');
      const navH = (!keyboardOpen && navEl) ? navEl.offsetHeight : 0;
      const headerEl = document.querySelector("header");
      const headerH = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 64;
      const top = offTop + headerH;
      const height = Math.max(200, Math.round(vh - navH - headerH));
      setMobileBox({ top, height });
      if (followRef.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    };
    apply();
    const raf = requestAnimationFrame(apply);
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      cancelAnimationFrame(raf);
      document.body.classList.remove("pm-support-lock");
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
    };
  }, [mobileActive]);

  const isAdmin = role === "admin";
  const quick = isAdmin ? ADMIN_QUICK : MEMBER_QUICK;
  const firstName = isAdmin ? "Admin" : (user?.full_name?.split(" ")[0] || "Trader");
  const welcome = isAdmin
    ? "Hi Admin! I'm your Admin Helper. Paste 8 lines of member details to add a member, or attach product image(s) with the labeled product details to add a Reward Mall product — I validate, show a preview, and save only after your confirmation."
    : `Hi ${firstName}! Welcome to PlusMaker Support. I'm here to help you understand and navigate your PlusMaker dashboard. What can I help you with today?`;

  useEffect(() => {
    if (!(fullPage || open) || loaded) return;
    setLoaded(true);
    api.get("/support/history")
      .then((res) => {
        const past = (res.data.messages || []).map((m) => ({ role: m.role, content: m.content, at: m.at, kind: m.kind, draft_id: m.draft_id, job_id: m.job_id }));
        setMessages(past.length ? past : [{ role: "assistant", content: welcome }]);
      })
      .catch(() => setMessages([{ role: "assistant", content: welcome }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded, welcome]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    followRef.current = near;
    setAtBottom((prev) => (prev === near ? prev : near));
  };

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    followRef.current = true;
    setAtBottom(true);
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Collapsible mobile composer: a physical tap expands then focuses synchronously
  // (flushSync) so iOS actually raises the keyboard. Unsent text is preserved across toggles.
  const expandComposer = () => {
    flushSync(() => setComposerExpanded(true));
    const el = isAdmin ? composerRef.current : clientInputRef.current;
    el?.focus();
  };
  const collapseComposer = () => {
    const el = isAdmin ? composerRef.current : clientInputRef.current;
    el?.blur();
    setComposerExpanded(false);
  };

  // Follow the stream only while the user is already near the bottom; never animate per token.
  useEffect(() => {
    if (followRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Reset follow + jump to latest when the panel (re)opens or history finishes loading.
  useEffect(() => {
    if (!(fullPage || open)) return;
    followRef.current = true;
    setAtBottom(true);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, [open, fullPage, loaded]);

  // Auto-grow the admin composer on mobile: 1 line → up to 3 lines, then scroll inside.
  useEffect(() => {
    const el = composerRef.current;
    if (!el || !mobileActive || !isAdmin) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, [input, mobileActive, isAdmin]);

  const appendAssistantDelta = (token) => {
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + token };
      return copy;
    });
  };

  const pickImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    setAttachments(files.map((file) => ({ file, preview: URL.createObjectURL(file) })));
    e.target.value = "";
  };

  const runIntent = async (intent, ibQuery) => {
    try {
      if (intent === "asst_regs" || intent === "registrations_today") {
        const { data } = await api.get("/admin/assistant/registrations-today");
        setMessages((m) => [...m, { role: "assistant", kind: "asst_regs", asst: data, content: "" }]);
      } else if (intent === "asst_pending" || intent === "indicator_pending") {
        const { data } = await api.get("/admin/assistant/indicator-requests");
        setMessages((m) => [...m, { role: "assistant", kind: "asst_pending", asst: data, content: "" }]);
      } else if (intent === "ib_clients") {
        if (!ibQuery) { setMessages((m) => [...m, { role: "assistant", content: "Which IB? Please include the IB name or code." }]); return true; }
        const { data } = await api.get(`/admin/assistant/ib-clients?q=${encodeURIComponent(ibQuery)}`);
        setMessages((m) => [...m, { role: "assistant", kind: "asst_ib", asst: data, content: "" }]);
      } else if (intent === "asst_redemptions") {
        const { data } = await api.get("/admin/assistant/redemptions-summary");
        setMessages((m) => [...m, { role: "assistant", kind: "asst_redemptions", asst: data, content: "" }]);
      } else if (intent === "asst_luckydraw") {
        const { data } = await api.get("/admin/assistant/lucky-draw-winners-summary");
        setMessages((m) => [...m, { role: "assistant", kind: "asst_luckydraw", asst: data, content: "" }]);
      } else if (intent === "asst_renewals") {
        const { data } = await api.get("/admin/indicator-requests?status=renewal_pending");
        setMessages((m) => [...m, { role: "assistant", kind: "asst_renewals", asst: data, content: "" }]);
      } else if (intent === "asst_indaccess") {
        const email = window.prompt("Enter the client's EXACT email to look up their active indicator access:");
        if (!email || !email.trim()) return true;
        const { data } = await api.get(`/admin/assistant/indicator-access?email=${encodeURIComponent(email.trim())}`);
        setMessages((m) => [...m, { role: "assistant", kind: "asst_indaccess", asst: data, content: "" }]);
      } else { return false; }
      return true;
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(err) }]);
      return true;
    }
  };

  // Shared Add-Product / Create-Reward-Draft flow: draft → review → explicit confirm → UNPUBLISHED reward.
  const createProductDraft = async (instruction, imgs) => {
    const fd = new FormData();
    fd.append("instruction", instruction || "");
    (imgs || []).forEach((a) => fd.append("files", a.file));
    const resp = await fetch(`${API}/api/support/product-drafts`, { method: "POST", credentials: "include", body: fd });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      setMessages((m) => [...m, { role: "assistant", content: err.detail || "Could not prepare a product draft" }]);
      return;
    }
    const d = await resp.json();
    if (d.mode === "job" && d.job?.id) {
      setMessages((m) => [...m, { role: "assistant", kind: "job", job_id: d.job.id, content: "Processing your product image(s) — drafts will appear below for your review. Nothing is created until you confirm." }]);
    } else {
      setMessages((m) => [...m, { role: "assistant", kind: "draft", draft_id: d.draft.id, content: "I've prepared a product draft for your review — nothing is created until you confirm." }]);
    }
  };

  const sendHelper = async (content, imgs) => {
    setMessages((m) => [...m, { role: "user", content: content || `Attached ${imgs.length} product image(s)`, images: imgs.map((a) => a.preview) }]);
    setInput("");
    setAttachments([]);
    try {
      // Classify the admin's Malay/English sentence first; facts still come from the backend.
      let intent = "unknown", ibQuery = "";
      if (content) {
        try { const c = await api.post("/admin/assistant/query", { text: content }); intent = c.data?.intent || "unknown"; ibQuery = c.data?.ib_query || ""; } catch { /* fall through to product path */ }
      }
      if (intent !== "add_product" && intent !== "unknown") {
        await runIntent(intent, ibQuery);
        setBusy(false);
        return;
      }
      // Add Product — the ONLY product flow: draft → review → explicit confirm → UNPUBLISHED reward.
      if (intent === "add_product" || imgs.length > 0) {
        await createProductDraft(content, imgs);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: "Try: “indicator pending request”, “client register hari ni”, “client under IB <name>”, or describe / attach a product image to add a new reward." }]);
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(err) }]);
    }
    setBusy(false);
  };

  const send = async (text, quickKey) => {
    const content = (text || "").trim();
    if ((!content && !attachments.length) || busy) return;
    setBusy(true);
    if (isAdmin && quickKey) {
      if (quickKey === "asst_ib") {
        const q = window.prompt("Enter the IB name or code to find their clients:");
        if (!q || !q.trim()) { setBusy(false); return; }
        setMessages((m) => [...m, { role: "user", content: `Clients under IB: ${q.trim()}` }]);
        await runIntent("ib_clients", q.trim());
        setBusy(false);
        return;
      }
      if (quickKey === "asst_draft") {
        const desc = window.prompt("Describe the reward to draft (name + details), or leave blank to attach product image(s) instead:");
        setMessages((m) => [...m, { role: "user", content: (desc && desc.trim()) ? desc.trim() : "Create a reward draft" }]);
        if (desc && desc.trim()) {
          await createProductDraft(desc.trim(), []);
        } else {
          setMessages((m) => [...m, { role: "assistant", content: "Attach product image(s) with the labeled details using the attach button, then send — I'll prepare a draft for your review. Nothing is created until you confirm." }]);
        }
        setBusy(false);
        return;
      }
      setMessages((m) => [...m, { role: "user", content }]);
      await runIntent(quickKey, "");
      setBusy(false);
      return;
    }
    if (isAdmin && !quickKey) {
      await sendHelper(content, attachments);
      return;
    }
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);

    if (quickKey) {
      try {
        const res = await api.post("/support/chat", { message: content, quick_key: quickKey });
        setMessages((m) => [...m, { role: "assistant", content: res.data.reply, kind: res.data.kind }]);
      } catch (err) {
        setMessages((m) => [...m, { role: "assistant", content: formatApiError(err) }]);
      }
      setBusy(false);
      return;
    }

    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/api/support/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: content }),
      });
      if (!resp.ok || !resp.body) throw new Error("request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") continue;
          try {
            const { t } = JSON.parse(data);
            if (t) appendAssistantDelta(t);
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Support is temporarily unavailable. Please try again shortly." };
        return copy;
      });
    }
    setBusy(false);
  };

  if (!fullPage && hidden) return null;

  const startDrag = (e, fromFab) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: window.innerWidth - rect.right,
      startBottom: window.innerHeight - rect.bottom,
      moved: false,
      fromFab,
    };
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const onDragMove = (e) => {
    const st = dragState.current;
    if (!st || !containerRef.current) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) st.moved = true;
    const rect = containerRef.current.getBoundingClientRect();
    const m = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let right = st.startRight - dx;
    let bottom = st.startBottom - dy;
    right = Math.min(Math.max(right, m), Math.max(m, vw - rect.width - m));
    bottom = Math.min(Math.max(bottom, m), Math.max(m, vh - rect.height - m));
    setPos({ right, bottom });
  };

  const endDrag = (e, fromFab) => {
    const st = dragState.current;
    dragState.current = null;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    if (st && !st.moved && fromFab) setOpen((o) => !o);
  };

  const closeAssistant = () => {
    try { sessionStorage.setItem("pm_support_hidden", "1"); } catch {}
    setHidden(true);
  };

  const containerStyle = pos
    ? { right: pos.right, bottom: pos.bottom, transition: dragging || prefersReduced ? "none" : "right 180ms ease, bottom 180ms ease" }
    : undefined;

  const messagesPane = (
    <div className="flex-1 min-h-0 relative">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={`h-full overflow-y-auto overscroll-contain px-4 ${mobileActive ? "py-3 space-y-2" : "py-4 space-y-3"}`}
        style={{ WebkitOverflowScrolling: "touch" }}
        data-testid="support-messages"
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`support-message-${i}`} data-role={m.role}>
            <div
              className={`max-w-[92%] rounded-2xl px-4 py-2.5 text-xs leading-relaxed whitespace-pre-line ${
                m.role === "user"
                  ? "bg-[#D4AF37] text-black rounded-br-sm"
                  : "bg-[#121318] border border-[#222530] text-[#E2E8F0] rounded-bl-sm"
              }`}
            >
              {m.images?.length > 0 && (
                <div className="flex gap-1.5 flex-wrap mb-2" data-testid={`support-message-images-${i}`}>
                  {m.images.map((src, j) => <img key={j} src={src} alt="" className="h-12 w-12 object-cover rounded-md border border-black/20" />)}
                </div>
              )}
              {m.content}
              {m.role === "assistant" && busy && i === messages.length - 1 && !m.content && (
                <span className="inline-block animate-pulse text-[#D4AF37]">●●●</span>
              )}
              {m.kind === "draft" && m.draft_id && <DraftMessage draftId={m.draft_id} />}
              {m.kind === "job" && m.job_id && <JobMessage jobId={m.job_id} />}
              {m.kind === "helper_member" && <HelperMemberCard preview={m.preview} />}
              {(m.kind === "asst_regs" || m.kind === "asst_pending" || m.kind === "asst_renewals" || m.kind === "asst_ib" || m.kind === "asst_redemptions" || m.kind === "asst_luckydraw" || m.kind === "asst_indaccess") && <AsstCard kind={m.kind} data={m.asst} onToast={(t) => setMessages((mm) => [...mm, { role: "assistant", content: t }])} />}
            </div>
          </div>
        ))}
      </div>
      {!atBottom && (
        <button
          type="button"
          onClick={jumpToBottom}
          className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-[#D4AF37] text-black shadow-[0_6px_20px_rgba(212,175,55,0.4)] flex items-center justify-center z-10 transition-transform hover:scale-105"
          data-testid="support-jump-bottom"
          aria-label="Jump to latest"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      )}
    </div>
  );

  const attachmentsPane = isAdmin && attachments.length > 0 && (
    <div className="px-4 pb-2 flex gap-2 flex-wrap" data-testid="support-attachments">
      {attachments.map((a, i) => (
        <div key={i} className="relative">
          <img src={a.preview} alt="" className="h-12 w-12 object-cover rounded-md border border-[#D4AF37]/40" data-testid={`support-attachment-${i}`} />
          <button
            onClick={() => setAttachments((list) => list.filter((_, j) => j !== i))}
            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center"
            data-testid={`support-attachment-remove-${i}`}
            aria-label="Remove image"
          >×</button>
        </div>
      ))}
      <span className="self-center text-[10px] text-[#64748B] font-mono">{attachments.length} image(s) ready</span>
    </div>
  );

  const quickButtons = quick.map((q) => (
    <button
      key={q.key}
      onClick={() => { send(q.label, q.key); setActionsOpen(false); }}
      disabled={busy}
      className="text-[11px] text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50"
      data-testid={`support-quick-${q.key}`}
    >
      {q.label}
    </button>
  ));

  const quickPane = quick.length > 0 && (
    <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="support-quick-questions">
      {quickButtons}
    </div>
  );

  // Full-page actions: desktop shows the quick buttons inline (unchanged); mobile
  // collapses them behind a compact "Show actions / Hide actions" toggle (default collapsed).
  const actionsPane = quick.length > 0 && (
    <div className="shrink-0" data-testid="support-actions">
      <div className="md:hidden pb-2">
        <button
          type="button"
          onClick={() => setActionsOpen((o) => !o)}
          aria-expanded={actionsOpen}
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors"
          data-testid="support-actions-toggle"
        >
          <ListChecks className="h-3.5 w-3.5" />
          {actionsOpen ? "Hide actions" : "Show actions"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${actionsOpen ? "rotate-180" : ""}`} />
        </button>
        {actionsOpen && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="support-actions-list">
            {quickButtons}
          </div>
        )}
      </div>
      <div className="hidden md:flex flex-wrap gap-2 pb-2" data-testid="support-actions-desktop">
        {quickButtons}
      </div>
    </div>
  );

  const composerCollapsed = mobileActive && !composerExpanded;

  const formPane = (
    <form
      onSubmit={(e) => { e.preventDefault(); send(input, null); }}
      className={fullPage ? "shrink-0 flex items-end gap-2 pt-3 pb-[max(8px,env(safe-area-inset-bottom,0px))]" : "flex items-center gap-2 px-4 py-3 border-t border-[#222530]"}
    >
      {composerCollapsed ? (
        <button
          type="button"
          onClick={expandComposer}
          className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl border border-[#D4AF37]/40 bg-[#0B0C10] text-[#D4AF37] font-medium text-sm active:scale-[0.99] transition-transform"
          data-testid="support-composer-open"
        >
          <MessageCircle className="h-4 w-4" /> Message
        </button>
      ) : (
        <>
          {mobileActive && (
            <button
              type="button"
              onClick={collapseComposer}
              className="h-12 w-10 rounded-xl border border-[#222530] text-[#94A3B8] flex items-center justify-center shrink-0 transition-colors hover:text-white"
              data-testid="support-composer-collapse"
              aria-label="Hide keyboard"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          )}
          {isAdmin && (
            <>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={pickImages} data-testid="support-image-input" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className={`border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${fullPage ? "h-12 w-12 rounded-xl" : "h-9 w-9 rounded-lg"}`}
                data-testid="support-attach-button"
                aria-label="Attach product images"
              >
                <Paperclip className={fullPage ? "h-5 w-5" : "h-4 w-4"} />
              </button>
            </>
          )}
          {isAdmin ? (
            <textarea
              ref={composerRef}
              className={`input-lux flex-1 resize-none ${fullPage ? (mobileActive ? "!py-2.5 text-sm max-h-24 overflow-y-auto" : "!py-3 text-sm") : "!py-2.5 text-xs"}`}
              rows={mobileActive ? 1 : 2}
              placeholder="Paste member or product details here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input, null); } }}
              maxLength={5000}
              data-testid="support-input"
            />
          ) : (
            <input
              ref={clientInputRef}
              className={`input-lux flex-1 ${fullPage ? (mobileActive ? "!py-2.5 text-sm" : "!py-3.5 text-sm") : "!py-2.5 text-xs"}`}
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={1000}
              data-testid="support-input"
            />
          )}
          <button
            type="submit"
            disabled={busy || (!input.trim() && !attachments.length)}
            className={`btn-gold flex items-center justify-center shrink-0 disabled:opacity-50 ${fullPage ? "h-12 w-12 rounded-xl" : "h-9 w-9 rounded-lg"}`}
            data-testid="support-send-button"
            aria-label="Send"
          >
            <Send className={fullPage ? "h-5 w-5" : "h-4 w-4"} />
          </button>
        </>
      )}
    </form>
  );

  if (fullPage) {
    return (
      <div
        ref={pageRef}
        className={`flex flex-col ${mobileActive ? "fixed left-0 right-0 z-30 bg-background px-4" : "h-[calc(100dvh_-_11rem_-_env(safe-area-inset-bottom,0px))] lg:h-[calc(100vh_-_8rem)]"}`}
        style={mobileActive && mobileBox ? { top: mobileBox.top, height: mobileBox.height } : undefined}
        data-testid="support-page"
      >
        <style>{SUPPORT_CSS}</style>

        <div className="hidden md:flex shrink-0 items-center gap-3 pb-5">
          <span className="h-11 w-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center shrink-0">
            <Headphones className="h-5 w-5 text-[#D4AF37]" />
          </span>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight" data-testid="support-page-title">
            Plusmaker Support
          </h1>
        </div>

        {!isAdmin && (
          <>
            <div
              className="hidden md:flex shrink-0 mb-4 rounded-xl border border-[#222530] border-l-4 border-l-[#D4AF37] bg-[#0B0C10]/80 px-4 sm:px-5 py-4 gap-3"
              data-testid="support-client-notice"
            >
              <Info className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#D4AF37] mb-1">Welcome to Plusmaker Support</p>
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                  Ask me anything about PlusMaker and your dashboard. If I can’t answer your question or you need further assistance, please contact{" "}
                  <a href="mailto:support@plusmaker.club" className="text-[#D4AF37] hover:underline font-medium" data-testid="support-contact-email">support@plusmaker.club</a>.
                </p>
              </div>
            </div>
            <div className="md:hidden shrink-0 mb-2" data-testid="support-client-strip">
              <button
                type="button"
                onClick={() => setInfoOpen((o) => !o)}
                aria-expanded={infoOpen}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#222530] border-l-2 border-l-[#D4AF37] bg-[#0B0C10]/80 px-3 py-2 text-left"
                data-testid="support-client-strip-toggle"
              >
                <span className="flex items-center gap-2 text-xs font-medium text-[#D4AF37]">
                  <Info className="h-3.5 w-3.5 shrink-0" /> Support info &amp; contact
                </span>
                <ChevronDown className={`h-3.5 w-3.5 text-[#94A3B8] transition-transform duration-200 ${infoOpen ? "rotate-180" : ""}`} />
              </button>
              {infoOpen && (
                <p className="mt-1.5 px-3 py-2 rounded-lg border border-[#222530] bg-[#0B0C10]/60 text-xs text-[#94A3B8] leading-relaxed" data-testid="support-client-strip-body">
                  Ask me anything about PlusMaker and your dashboard. Need more help? Contact{" "}
                  <a href="mailto:support@plusmaker.club" className="text-[#D4AF37] hover:underline font-medium" data-testid="support-contact-email">support@plusmaker.club</a>.
                </p>
              )}
            </div>
          </>
        )}

        {messagesPane}
        {attachmentsPane}
        {actionsPane}
        {formPane}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`fixed z-50 flex flex-col items-end gap-3 ${pos ? "" : "support-launcher-default"}`} style={containerStyle} data-testid="support-widget">
      <style>{SUPPORT_CSS}</style>
      {open && (
        <div
          className="w-[calc(100vw-40px)] sm:w-96 card-lux flex flex-col overflow-hidden"
          style={{ height: "min(560px, calc(100vh - 120px))" }}
          data-testid="support-panel"
        >
          <div
            onPointerDown={(e) => startDrag(e, false)}
            onPointerMove={onDragMove}
            onPointerUp={(e) => endDrag(e, false)}
            style={{ touchAction: "none" }}
            className="flex items-center justify-between px-5 py-4 border-b border-[#222530] bg-[#0B0C10] cursor-move select-none"
            data-testid="support-drag-handle"
          >
            <div className="flex items-center gap-2.5">
              <LifeBuoy className="h-4 w-4 text-[#D4AF37]" />
              <div>
                <p className="text-sm font-semibold text-white leading-tight" data-testid="support-panel-title">PlusMaker Support</p>
                <p className="text-[10px] text-[#64748B] font-mono tracking-widest uppercase">Dashboard Assistant</p>
              </div>
            </div>
            <button onClick={closeAssistant} onPointerDown={(e) => e.stopPropagation()} className="text-[#94A3B8] hover:text-white p-1 transition-colors" data-testid="support-close" aria-label="Close dashboard assistant">
              <X className="h-4 w-4" />
            </button>
          </div>
          {messagesPane}
          {attachmentsPane}
          {quickPane}
          {formPane}
        </div>
      )}
      <button
        onPointerDown={(e) => startDrag(e, true)}
        onPointerMove={onDragMove}
        onPointerUp={(e) => endDrag(e, true)}
        className={`pm-support-fab ${!dragging ? "pm-idle" : ""} rounded-full btn-gold shadow-[0_8px_30px_rgba(212,175,55,0.35)] flex items-center justify-center transition-transform ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        style={{ height: 52, width: 52, touchAction: "none" }}
        data-testid="support-fab"
        aria-label="PlusMaker Support"
      >
        {open ? <X className="h-5 w-5" /> : <LifeBuoy className="h-5 w-5" />}
      </button>
    </div>
  );
}
